"use client";

import React, {
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { useHotelMapSearch } from "./hooks/useHotelMapSearch";
import MapCardItem from "@/components/pages/hotel/cards/MapCardItem";
import FiltersPanel from "@/components/pages/hotel/search/FiltersPanel";
import FilterLoader from "@/components/pages/hotel/search/FilterLoader";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { formatCurrency } from "@/utils/currencyFormatter";

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 39.9334, lng: 32.8597 };
const MIN_ZOOM_FOR_RESULTS = 8;

function getSafeAreaInsets() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const probe = document.createElement("div");
  probe.style.cssText = [
    "position:fixed",
    "visibility:hidden",
    "pointer-events:none",
    "top:0",
    "left:0",
    "padding-top:env(safe-area-inset-top, 0px)",
    "padding-right:env(safe-area-inset-right, 0px)",
    "padding-bottom:env(safe-area-inset-bottom, 0px)",
    "padding-left:env(safe-area-inset-left, 0px)",
  ].join(";");
  document.body.appendChild(probe);
  const styles = window.getComputedStyle(probe);
  const insets = {
    top: parseFloat(styles.paddingTop) || 0,
    right: parseFloat(styles.paddingRight) || 0,
    bottom: parseFloat(styles.paddingBottom) || 0,
    left: parseFloat(styles.paddingLeft) || 0,
  };
  document.body.removeChild(probe);
  return insets;
}

/** Kartlarla aynı fiyat formatı (sembol, 0 ondalık) */
function formatPriceForMarker(price, currencyCode = "TRY") {
  if (price === null || price === undefined || Number.isNaN(Number(price)))
    return "—";
  const code = currencyCode === "TL" ? "TRY" : currencyCode || "TRY";
  try {
    return formatCurrency(Number(price), code, {
      showSymbol: true,
      showCode: false,
      decimals: 0,
    });
  } catch {
    return `${Math.round(Number(price)).toLocaleString("tr-TR")} TL`;
  }
}

/** Fiyat balonu: text-sm text-foreground font-semibold leading-19, border-border h-12 px-2 py-[5px], rounded-lg, shadow; hover: text-white bg-primary */
const MARKER_BORDER = "#d0d0dc";
const MARKER_FOREGROUND = "#1d1e20";
const MARKER_PRIMARY = "#01007f";
const ACTIVE_MARKER_Z_INDEX = 5000;
const DEFAULT_MARKER_Z_INDEX = 1;

function createPriceMarkerIcon(price, currencyCode = "TRY", isHover = false) {
  const text = formatPriceForMarker(price, currencyCode);
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  const w = 88;
  const h = 44;
  const anchorX = w / 2;
  const anchorY = h;
  const bodyBottom = 36;
  const arrowHalf = 8;
  const rx = 8;
  const fill = isHover ? MARKER_PRIMARY : "#fff";
  const stroke = isHover ? MARKER_PRIMARY : MARKER_BORDER;
  const textFill = isHover ? "#fff" : MARKER_FOREGROUND;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <defs>
        <filter id="priceShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="rgba(20,20,20,0.10)" flood-opacity="1"/>
        </filter>
      </defs>
      <path d="M ${rx} 0 L ${w - rx} 0 Q ${w} 0 ${w} ${rx} L ${w} ${bodyBottom - rx} Q ${w} ${bodyBottom} ${w - rx} ${bodyBottom} L ${anchorX + arrowHalf} ${bodyBottom} L ${anchorX} ${h} L ${anchorX - arrowHalf} ${bodyBottom} L ${rx} ${bodyBottom} Q 0 ${bodyBottom} 0 ${bodyBottom - rx} L 0 ${rx} Q 0 0 ${rx} 0 Z" fill="${fill}" stroke="${stroke}" stroke-width="1" filter="url(#priceShadow)"/>
      <text x="${anchorX}" y="24" text-anchor="middle" font-weight="600" font-size="14" font-family="Turna,system-ui,sans-serif" fill="${textFill}" style="line-height:19px">${escaped}</text>
    </svg>
  `;
  const base64 =
    typeof window !== "undefined"
      ? window.btoa(unescape(encodeURIComponent(svg)))
      : "";
  return {
    url: `data:image/svg+xml;base64,${base64}`,
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(anchorX, anchorY),
  };
}

/** Cluster marker: beyaz arka plan, sayı metni text-foreground font-semibold text-[23px] */
const CLUSTER_FOREGROUND_COLOR = "#1d1e20";
function createClusterRenderer() {
  return {
    render({ count, position }, _stats, _map) {
      const svg = window.btoa(
        `<svg fill="#ffffff" stroke="#e5e7eb" stroke-width="1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="26" />
        </svg>`,
      );
      return new window.google.maps.Marker({
        position,
        icon: {
          url: `data:image/svg+xml;base64,${svg}`,
          scaledSize: new window.google.maps.Size(56, 56),
          anchor: new window.google.maps.Point(28, 28),
        },
        label: {
          text: String(count),
          color: CLUSTER_FOREGROUND_COLOR,
          fontSize: "23px",
          fontWeight: "600",
          fontFamily: "Turna, system-ui, sans-serif",
        },
        zIndex: 1000 + count,
      });
    },
  };
}

const getBoardLabel = (boardId, t) => {
  if (!boardId) return null;
  const boardMap = {
    BB: "boardBB",
    RO: "boardRO",
    AI: "boardAI",
    HB: "boardHB",
    FB: "boardFB",
    UAI: "boardUAI",
  };
  const key = boardMap[boardId];
  return key ? t(key) : null;
};

const getRatingText = (score, t) => {
  if (typeof score !== "number" || score <= 0) return null;
  if (score >= 9) return t("Excellent");
  if (score >= 8) return t("Great");
  if (score >= 7) return t("VeryGood");
  if (score >= 6) return t("Good");
  return null;
};

const formatStayDuration = (checkIn, checkOut, t) => {
  if (!checkIn || !checkOut) return "";
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const diffTime = Math.max(end.getTime() - start.getTime(), 0);
  const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return t("nights", { count: nights });
};

/**
 * Map API MapHotelList item'ını SearchCardItem'ın beklediği formata çevirir.
 */
function mapHotelToCardItem(hotel, meta, t) {
  const price = hotel?.Price;
  const netPrice = typeof price?.NetPrice === "number" ? price.NetPrice : null;
  const grossPrice =
    typeof price?.GrossPrice === "number" ? price.GrossPrice : null;
  const discountRate =
    typeof price?.DiscountRate === "number" ? price.DiscountRate : null;
  const hasDiscount =
    netPrice !== null &&
    netPrice !== undefined &&
    grossPrice !== null &&
    grossPrice !== undefined &&
    Math.round(netPrice) !== Math.round(grossPrice);
  const surveyScore = hotel?.SurveyScore;
  const isValidRating = typeof surveyScore === "number" && surveyScore > 0;
  const images = hotel?.Images?.length ? hotel.Images : [];

  return {
    id: hotel?.TurnaHotelId || hotel?.HotelId,
    hotelId: hotel?.HotelId,
    Currency: price?.Currency || "TRY",
    link: `/hotel/${hotel?.Slug || hotel?.HotelId || ""}`,
    name: hotel?.Name || hotel?.Slug || t("hotelFallback"),
    starRating: hotel?.StarNumber || 0,
    rating: isValidRating ? surveyScore : null,
    ratingText: isValidRating ? getRatingText(surveyScore, t) : null,
    reviewCount: 0,
    location: hotel?.LocationName || "",
    breakfast: getBoardLabel(hotel?.BoardId, t),
    amenities: (hotel?.Amenities || []).map((amenity) => ({
      Code: amenity.Code,
      Name: amenity.AmenityName || amenity.Name,
    })),
    isRefundable: hotel?.IsRefundable ?? null,
    checkIn: meta?.checkIn || null,
    checkOut: meta?.checkOut || null,
    CheckinDate: meta?.checkIn || null,
    CheckoutDate: meta?.checkOut || null,
    roomTypeDescription: null,
    price: netPrice ?? grossPrice,
    priceOriginal:
      hasDiscount && grossPrice !== null && grossPrice !== undefined
        ? grossPrice
        : null,
    discountRate: hasDiscount ? discountRate : null,
    duration: formatStayDuration(meta?.checkIn, meta?.checkOut, t),
    guestSummary: meta?.occupancyText || "",
    imageUrl: images.length ? images : ["/images/dummy/hotel-placeholder.jpg"],
    IsFavorited: hotel?.IsFavorited || false,
    FavoriteId: hotel?.FavoriteId || null,
    Address: null,
    Tags: [],
    MarketType: null,
    ProviderId: hotel?.ProviderId ?? null,
    IsCampaignApplied: hotel?.IsCampaignApplied === true,
  };
}

export default function HotelSearchMapView({
  initialBoundingBox,
  checkinDate,
  checkoutDate,
  occupancies = [],
  occupancyText = "",
  userFilterInput = null,
  preferences = {},
  destination,
  _mapTitle = "",
  onSelectHotel,
  filters = {},
  filterOptions = {},
  onFilterChange,
  order = 0,
  _onSortChange,
  _discountRate = 0,
  _onCampaignChange,
  filterLoading = false,
  itemListId,
  itemListName,
  passengers,
}) {
  const t = useTranslationDetector("hotel");
  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const markersRef = useRef([]);
  const hoverCloseTimeoutRef = useRef(null);
  const hoveredLatLngRef = useRef(null);
  const hoverCardRef = useRef(null);
  const markerClickHandledRef = useRef(false);
  const mapClickListenerRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(null);
  const [hoveredHotel, setHoveredHotel] = useState(null);
  const [hoveredPixel, setHoveredPixel] = useState(null);
  const [hoveredCardHotelId, setHoveredCardHotelId] = useState(null);
  const [hoveredMarkerHotelId, setHoveredMarkerHotelId] = useState(null);

  const { mapHotels, loading, error, fetchMapHotels } = useHotelMapSearch({
    checkinDate,
    checkoutDate,
    occupancies,
    userFilterInput,
    preferences,
    destination,
  });

  const meta = useMemo(
    () => ({
      checkIn: checkinDate,
      checkOut: checkoutDate,
      occupancyText: occupancyText || "",
    }),
    [checkinDate, checkoutDate, occupancyText],
  );

  const normalizedHotels = useMemo(
    () => mapHotels.map((h) => mapHotelToCardItem(h, meta, t)),
    [mapHotels, meta, t],
  );

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-hotel-script",
    googleMapsApiKey:
      process.env.NEXT_PUBLIC_MAPS_API_KEY || process.env.MAPS_API_KEY || "",
  });

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;

      if (initialBoundingBox?.topLeft && initialBoundingBox?.bottomRight) {
        const bounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(
            initialBoundingBox.topLeft.latitude,
            initialBoundingBox.topLeft.longitude,
          ),
          new window.google.maps.LatLng(
            initialBoundingBox.bottomRight.latitude,
            initialBoundingBox.bottomRight.longitude,
          ),
        );
        map.fitBounds(bounds);
        const boxForApi = {
          topLeft: {
            latitude: initialBoundingBox.topLeft.latitude,
            longitude: initialBoundingBox.topLeft.longitude,
          },
          bottomRight: {
            latitude: initialBoundingBox.bottomRight.latitude,
            longitude: initialBoundingBox.bottomRight.longitude,
          },
        };
        fetchMapHotels(boxForApi);
      }

      map.addListener("idle", () => {
        const zoom = map.getZoom();
        setZoomLevel(typeof zoom === "number" ? zoom : null);
      });

      // Haritada boş alana tıklanınca kartı kapat; marker tıklanınca kapatma
      if (mapClickListenerRef.current && window.google?.maps?.event) {
        window.google.maps.event.removeListener(mapClickListenerRef.current);
      }
      mapClickListenerRef.current = map.addListener("click", () => {
        const wasMarker = markerClickHandledRef.current;
        markerClickHandledRef.current = false;
        setTimeout(() => {
          if (!markerClickHandledRef.current && !wasMarker) {
            hoveredLatLngRef.current = null;
            setHoveredHotel(null);
            setHoveredPixel(null);
          }
        }, 0);
      });
    },
    [initialBoundingBox, fetchMapHotels],
  );

  const onMapUnmount = useCallback(() => {
    if (hoverCloseTimeoutRef.current)
      clearTimeout(hoverCloseTimeoutRef.current);
    setHoveredHotel(null);
    setHoveredPixel(null);
    setHoveredMarkerHotelId(null);
    hoveredLatLngRef.current = null;
    if (mapClickListenerRef.current && window.google?.maps?.event) {
      window.google.maps.event.removeListener(mapClickListenerRef.current);
      mapClickListenerRef.current = null;
    }
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    markersRef.current = [];
    mapRef.current = null;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof window === "undefined") return;

    const applyViewportPadding = () => {
      const insets = getSafeAreaInsets();
      const viewport = window.visualViewport;
      const hiddenBottom = viewport
        ? Math.max(
            window.innerHeight - (viewport.height + viewport.offsetTop),
            0,
          )
        : 0;
      map.setOptions({
        padding: {
          top: Math.ceil(insets.top + 12),
          right: Math.ceil(insets.right + 12),
          bottom: Math.ceil(Math.max(insets.bottom, hiddenBottom) + 12),
          left: Math.ceil(insets.left + 12),
        },
      });
    };

    applyViewportPadding();
    const viewport = window.visualViewport;
    window.addEventListener("resize", applyViewportPadding);
    viewport?.addEventListener("resize", applyViewportPadding);
    viewport?.addEventListener("scroll", applyViewportPadding);

    return () => {
      window.removeEventListener("resize", applyViewportPadding);
      viewport?.removeEventListener("resize", applyViewportPadding);
      viewport?.removeEventListener("scroll", applyViewportPadding);
    };
  }, [isLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const hotelsWithLocation = mapHotels.filter(
      (h) =>
        h?.GeoLocation?.Latitude !== null &&
        h?.GeoLocation?.Latitude !== undefined &&
        h?.GeoLocation?.Longitude !== null &&
        h?.GeoLocation?.Longitude !== undefined,
    );

    const normalizedByHotelId = new Map();
    normalizedHotels.forEach((n) => {
      const id = n.id ?? n.hotelId;
      if (id !== null && id !== undefined) {
        normalizedByHotelId.set(id, n);
        normalizedByHotelId.set(String(id), n);
        if (typeof id === "string" && /^\d+$/.test(id))
          normalizedByHotelId.set(Number(id), n);
      }
    });

    const updatePixelFromLatLng = (latLng) => {
      const mapDiv = map.getDiv();
      if (!mapDiv) return;
      const bounds = map.getBounds();
      if (!bounds) {
        const rect = mapDiv.getBoundingClientRect();
        setHoveredPixel({ x: rect.width / 2, y: rect.height / 2 });
        return;
      }
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const rect = mapDiv.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      const lngSpan = ne.lng() - sw.lng();
      const latSpan = ne.lat() - sw.lat();
      const x = lngSpan ? ((latLng.lng - sw.lng()) / lngSpan) * w : w / 2;
      const y = latSpan ? (1 - (latLng.lat - sw.lat()) / latSpan) * h : h / 2;
      setHoveredPixel({ x, y });
    };

    const markers = hotelsWithLocation.map((hotel) => {
      const price = hotel?.Price;
      const netPrice = price?.NetPrice ?? price?.GrossPrice;
      const currency = price?.Currency || "TRY";
      const marker = new window.google.maps.Marker({
        position: {
          lat: hotel.GeoLocation.Latitude,
          lng: hotel.GeoLocation.Longitude,
        },
        map,
        title: "",
        icon: createPriceMarkerIcon(netPrice, currency, false),
      });

      const hotelId = hotel.TurnaHotelId ?? hotel.HotelId;
      let normalizedHotel =
        normalizedByHotelId.get(hotelId) ??
        normalizedByHotelId.get(String(hotelId)) ??
        null;
      if (!normalizedHotel) {
        const idx = mapHotels.findIndex(
          (h) => (h.TurnaHotelId ?? h.HotelId) === hotelId,
        );
        if (idx >= 0 && normalizedHotels[idx])
          normalizedHotel = normalizedHotels[idx];
      }
      const latLng = {
        lat: hotel.GeoLocation.Latitude,
        lng: hotel.GeoLocation.Longitude,
      };

      marker.addListener("mouseover", () => {
        setHoveredMarkerHotelId(hotelId);
      });
      marker.addListener("mouseout", () => {
        setHoveredMarkerHotelId(null);
      });
      marker.addListener("click", () => {
        markerClickHandledRef.current = true;
        hoveredLatLngRef.current = latLng;
        setHoveredHotel(normalizedHotel || null);
        updatePixelFromLatLng(latLng);
        requestAnimationFrame(() => updatePixelFromLatLng(latLng));
        setTimeout(() => updatePixelFromLatLng(latLng), 50);
        setTimeout(() => {
          markerClickHandledRef.current = false;
        }, 50);
      });

      return marker;
    });

    const idleListener = map.addListener("idle", () => {
      if (hoveredLatLngRef.current)
        updatePixelFromLatLng(hoveredLatLngRef.current);
    });

    markersRef.current = markers;

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    if (markers.length > 0) {
      clustererRef.current = new MarkerClusterer({
        map,
        markers,
        renderer: createClusterRenderer(),
      });
    }

    return () => {
      if (typeof idleListener?.remove === "function") idleListener.remove();
    };
  }, [mapHotels, normalizedHotels, checkinDate, checkoutDate, occupancies]);

  useEffect(() => {
    const markers = markersRef.current;
    if (!markers?.length || !mapHotels?.length) return;
    const hotelsWithLocation = mapHotels.filter(
      (h) =>
        h?.GeoLocation?.Latitude !== null &&
        h?.GeoLocation?.Latitude !== undefined &&
        h?.GeoLocation?.Longitude !== null &&
        h?.GeoLocation?.Longitude !== undefined,
    );
    if (hotelsWithLocation.length !== markers.length) return;
    const selectedId =
      hoveredCardHotelId ?? hoveredHotel?.id ?? hoveredHotel?.hotelId;
    for (let i = 0; i < markers.length; i++) {
      const hotel = hotelsWithLocation[i];
      const hotelId = hotel.TurnaHotelId ?? hotel.HotelId;
      const price = hotel?.Price;
      const netPrice = price?.NetPrice ?? price?.GrossPrice;
      const currency = price?.Currency || "TRY";
      const isSelected =
        selectedId !== null &&
        selectedId !== undefined &&
        (String(hotelId) === String(selectedId) ||
          String(hotel.TurnaHotelId) === String(selectedId));
      const isHovered =
        hoveredMarkerHotelId !== null &&
        hoveredMarkerHotelId !== undefined &&
        (String(hotelId) === String(hoveredMarkerHotelId) ||
          String(hotel.TurnaHotelId) === String(hoveredMarkerHotelId));
      const isActive = isSelected || isHovered;
      markers[i].setIcon(createPriceMarkerIcon(netPrice, currency, isActive));
      markers[i].setZIndex(
        isActive ? ACTIVE_MARKER_Z_INDEX : DEFAULT_MARKER_Z_INDEX,
      );
    }
  }, [hoveredCardHotelId, hoveredHotel, hoveredMarkerHotelId, mapHotels]);

  useLayoutEffect(() => {
    if (
      !hoveredHotel ||
      !hoveredPixel ||
      !hoverCardRef.current ||
      !mapRef.current
    )
      return;
    const map = mapRef.current;
    const cardEl = hoverCardRef.current;
    const mapDiv = map.getDiv();
    if (!mapDiv) return;

    const cardRect = cardEl.getBoundingClientRect();
    const mapRect = mapDiv.getBoundingClientRect();

    let panX = 0;
    let panY = 0;
    if (cardRect.left < mapRect.left) panX += cardRect.left - mapRect.left;
    if (cardRect.right > mapRect.right) panX += cardRect.right - mapRect.right;
    if (cardRect.top < mapRect.top) panY += cardRect.top - mapRect.top;
    if (cardRect.bottom > mapRect.bottom)
      panY += cardRect.bottom - mapRect.bottom;

    if (panX !== 0 || panY !== 0) {
      map.panBy(panX, panY);
    }
  }, [hoveredHotel, hoveredPixel]);

  const handleSearchInThisArea = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    fetchMapHotels(
      {
        topLeft: { latitude: ne.lat(), longitude: sw.lng() },
        bottomRight: { latitude: sw.lat(), longitude: ne.lng() },
      },
      { isNewSearch: true },
    );
  }, [fetchMapHotels]);

  // Filtre değiştiğinde (sol panel veya arama sayfasında) mevcut bounds ile haritayı yeniden iste
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    fetchMapHotels(
      {
        topLeft: { latitude: ne.lat(), longitude: sw.lng() },
        bottomRight: { latitude: sw.lat(), longitude: ne.lng() },
      },
      { isNewSearch: true },
    );
  }, [userFilterInput, fetchMapHotels]);

  const zoomTooWide =
    zoomLevel !== null &&
    zoomLevel !== undefined &&
    zoomLevel < MIN_ZOOM_FOR_RESULTS;

  if (loadError) {
    return (
      <div className="text-foreground-muted flex h-full items-center justify-center p-4">
        {t("mapLoadError")}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-primary-weak/10 flex h-full items-center justify-center rounded-lg">
        <span className="text-foreground-muted">{t("mapLoading")}</span>
      </div>
    );
  }

  const zoomInLabel = t("zoomInToSeeResults");

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-0 overflow-hidden md:flex-row">
      <div className="border-primary-weak flex min-h-0 w-full flex-shrink-0 flex-col overflow-hidden border-r md:w-[712px] md:flex-row">
        {/* Filtre kolonu (246px) */}
        <div className="border-primary-weak flex min-h-0 w-full flex-shrink-0 flex-col overflow-hidden border-r bg-white md:w-[294px]">
          <div className="border-primary-weak flex-shrink-0 border-b bg-white px-6 pt-4 pb-2">
            <h2 className="text-foreground text-lg font-semibold">
              {t("filterTitle")}
            </h2>
          </div>
          <div className="scrollbar-hide min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
            {typeof onFilterChange === "function" && (
              <FiltersPanel
                filters={filters}
                onFilterChange={onFilterChange}
                filterOptions={filterOptions}
                currentSortId={String(order)}
              />
            )}
          </div>
        </div>

        {/* Liste kolonu (370px) */}
        <div className="bg-background-300 relative flex min-h-0 w-full flex-shrink-0 flex-col overflow-hidden md:w-[418px]">
          <div className="scrollbar-hide relative flex min-h-0 min-w-0 flex-1 flex-col space-y-3 overflow-x-hidden overflow-y-auto p-6">
            {(loading || filterLoading) && (
              <div className="pointer-events-none absolute inset-0 z-10 flex min-h-[200px] items-center justify-center">
                <FilterLoader
                  loading={true}
                  className="!sticky !mt-0 !h-auto"
                  innerClassName="!mt-0"
                />
              </div>
            )}
            <div
              className={`flex min-h-0 flex-1 flex-col gap-4 transition-opacity ${loading || filterLoading ? "pointer-events-none opacity-50" : "opacity-100"}`}
            >
              {!loading &&
                !filterLoading &&
                normalizedHotels.length === 0 &&
                !error && (
                  <p className="text-foreground-muted p-2 text-sm">
                    {t("mapBringHotelsHint")}
                  </p>
                )}
              {!loading && !filterLoading && error && (
                <p className="text-danger p-2 text-sm">
                  {error?.message || t("mapErrorDefault")}
                </p>
              )}
              {normalizedHotels.map((hotel) => (
                <div
                  key={hotel.id || hotel.hotelId}
                  className="border-primary-weak w-full min-w-0 flex-shrink-0 rounded-lg border bg-white p-4"
                  onMouseEnter={() =>
                    setHoveredCardHotelId(hotel.id ?? hotel.hotelId ?? null)
                  }
                  onMouseLeave={() => setHoveredCardHotelId(null)}
                >
                  <MapCardItem
                    hotelData={hotel}
                    onSelect={onSelectHotel}
                    itemListId={itemListId}
                    itemListName={itemListName}
                    passengers={passengers}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        className="relative h-full min-h-[250px] w-full flex-1 md:min-h-[400px]"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
        }}
      >
        <GoogleMap
          mapContainerStyle={{ ...MAP_CONTAINER_STYLE, minHeight: "250px" }}
          center={DEFAULT_CENTER}
          zoom={10}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          options={{
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            gestureHandling: "greedy",
          }}
        />
        {hoveredHotel && hoveredPixel && (
          <div
            className="pointer-events-none absolute inset-0 z-[100]"
            aria-hidden
          >
            <div
              ref={hoverCardRef}
              className="border-primary-weak pointer-events-auto absolute w-[341px] max-w-[90vw] overflow-hidden rounded-lg border bg-white p-4 shadow-lg"
              style={{
                left: hoveredPixel.x,
                top: hoveredPixel.y,
                transform: "translate(-50%, -100%) translateY(-24px)",
              }}
            >
              <MapCardItem
                hotelData={hoveredHotel}
                onSelect={onSelectHotel}
                itemListId={itemListId}
                itemListName={itemListName}
                passengers={passengers}
              />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={handleSearchInThisArea}
          disabled={loading}
          className="border-primary absolute top-4 left-1/2 flex h-[40px] w-[175px] -translate-x-1/2 cursor-pointer items-center gap-2 rounded-lg border bg-white px-6 py-2 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ boxShadow: "0 4px 8px 2px rgba(20, 20, 20, 0.03)" }}
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.far["magnifying-glass"]}
            className="text-primary h-4! w-4!"
          />
          <span className="text-primary text-sm font-semibold">
            {t("searchInThisArea")}
          </span>
        </button>
        {zoomTooWide && (
          <div className="text-foreground border-primary-weak absolute bottom-4 left-1/2 -translate-x-1/2 rounded-lg border bg-white/95 px-4 py-2 text-sm shadow-md">
            {zoomInLabel}
          </div>
        )}
      </div>
    </div>
  );
}
